import React from 'react';
import { askOrbitAction } from '../../components/AskOrbit';
import { aaiApi } from '../../services/aaiApi';
import { useAsync } from '../aai/useAsync';
import type { AaiRequest, AaiSkill, CompanyDashboard, OwnerOverview } from '../../types/aai';
import {
  Action, Chip, Divided, EmptyState, ErrorNote, ListRow, Loading, PageBody, PageHead, Panel, PanelHead, Stat,
} from '../../components/aa';
import { useRecordLinks } from '../../hooks/useRecordLinks';
import { OrbitRoomPanel } from '../orbit-room/OrbitRoomPanel';
// import { RegistryStrip } from './RegistryPanel';
// import { HomeResults } from '../../components/HomeResults';
// import { ResultsBody } from './ResultsPage';
import { nextFor, stateFor } from '../partner/work';
import { evidenceLine, needsChampion, openSettings, rankRequests, teamsNeedingSupport } from './champion';
import { ClientWorkCard } from '../employee/EmployeeHome';

/*
 * The AI Champion's Home: the numbers, the company as Orbit reads it, then the
 * priorities.
 *
 * Four plain counts under the title, the Orbit Room in full beneath them, then
 * the ranked priorities and the Quick Wins ready to scale. The Room used to sit
 * at the top of Company Context; it lives here now and Company Context keeps
 * its tabs.
 *
 * Commented out rather than removed, for now: the current-cycle strip, the Use
 * Case Registry strip and the Results block.
 */

const STATE_WORD: Record<string, string> = {
  pending: 'Clarity',
  needs_review: 'Clarity',
  in_progress: 'Planning',
  in_delivery: 'Building',
  ready_short: 'Ready',
};

const stateWord = (r: AaiRequest): string => {
  if (r.tool_skill_id) return STATE_WORD.ready_short;
  return STATE_WORD[stateFor(r)] || 'Planning';
};

const priorityChip = (r: AaiRequest): string => {
  if (r.est_value_band) return `${stateWord(r)} · ${r.est_value_band}`;
  return `${stateWord(r)} · no forecast`;
};

const Rank: React.FC<{ n: number }> = ({ n }) => (
  <span className="grid h-[37px] w-[37px] shrink-0 place-items-center rounded-full bg-canvas-inset font-sans text-[13px] font-bold tabular-nums text-ink">{n}</span>
);

/*
interface Cell { label: string; value: React.ReactNode; foot: string }

const CycleStrip: React.FC<{ title: string; cells: Cell[] }> = ({ title, cells }) => (
  <Panel className="mb-5">
    <PanelHead kicker="Current cycle" title={title} action={<Action size="sm" variant="quiet" arrow to="/portfolio">Compare priorities</Action>} />
    <div className="grid divide-y divide-line-soft border-t border-line-soft md:grid-cols-5 md:divide-x md:divide-y-0">
      {cells.map((c) => (
        <div key={c.label} className="px-5 py-5">
          <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">{c.label}</div>
          <div className="mt-2 text-[17px] font-bold leading-[24px] tracking-[-0.005em] text-ink">{c.value}</div>
          <div className="mt-1 text-[12px] text-ink-dim">{c.foot}</div>
        </div>
      ))}
    </div>
  </Panel>
);
*/

const ChampionHome: React.FC = () => {
  const clientId = localStorage.getItem('client_id') || '';
  const overview = useAsync<OwnerOverview>(() => aaiApi.getOwnerOverview(), 'Could not load the company portfolio.');
  const requests = useAsync<{ scope: string; requests: AaiRequest[] }>(() => aaiApi.listRequests(), 'Could not load company work.');
  const skills = useAsync<{ skills: AaiSkill[]; can_manage: boolean }>(() => aaiApi.listSkills(), 'Could not load Quick Wins.');
  const dashboard = useAsync<CompanyDashboard>(() => aaiApi.getDashboard(), 'Could not load company readiness.');
  const setOpenProject = useRecordLinks().project;

  if (overview.loading || requests.loading) return <PageBody><Loading label="Loading your portfolio" /></PageBody>;
  if (overview.error || !overview.data) return <PageBody><ErrorNote message={overview.error || 'Something went wrong.'} /></PageBody>;

  const data = overview.data;
  const all = requests.data?.requests || [];
  const ranked = rankRequests(all);
  const decisions = all.filter(needsChampion).length;
  const teams = teamsNeedingSupport(dashboard.data);
  const quickWins = (skills.data?.skills || []).filter((s) => s.published);

  return (
    <PageBody>
      <PageHead
        align="center"
        eyebrow="AI Champion"
        title="Dashboard"
        actions={(
          <>
            <Action variant="quiet" onClick={() => openSettings('plan')}>Work capacity</Action>
            {/* <Action arrow to="/portfolio">Portfolio</Action> */}
          </>
        )}
      />

      {localStorage.getItem('is_expert') === '1' && <ClientWorkCard />}

      <div className="mb-5 grid grid-cols-2 gap-3 xl:grid-cols-4">
        <Stat label="Decisions waiting" value={decisions} tone={decisions > 0 ? 'attention' : 'default'} />
        <Stat label="Opportunities ready" value={data.counts.opportunities} />
        <Stat label="Quick Wins" value={quickWins.length} />
        <Stat label="Teams needing support" value={teams.length} />
      </div>

      {clientId && (
        <div className="mb-5">
          <OrbitRoomPanel viewer="champion" clientId={clientId} />
        </div>
      )}

      {/* <CycleStrip title={cycleTitle} cells={cells} /> */}

      <div className="grid gap-5 lg:grid-cols-[1.55fr_1fr]">
        <Panel floor>
          <PanelHead
            kicker="Portfolio"
            title="Recommended priorities"
            action={(
              <span className="flex flex-wrap items-center gap-2">
                {/* Open requests door hidden for now.
                <Action size="sm" variant="quiet" arrow to="/requests">Open requests</Action>
                */}
                {/* <Action size="sm" variant="quiet" to="/portfolio">See portfolio</Action> */}
              </span>
            )}
          />
          {ranked.length === 0 && (
            <EmptyState title="No work to compare yet" reason="Priorities appear once people ask Orbit for something or the Solution Partner sends an opportunity." action={askOrbitAction()} />
          )}
          <Divided>
            {ranked.slice(0, 3).map((r, i) => {
              let badge: React.ReactNode = null;
              if (needsChampion(r)) badge = <Chip tone="warn" dot>Needs you</Chip>;
              return (
                <ListRow
                  key={r.id}
                  mark={<Rank n={i + 1} />}
                  title={r.title}
                  badge={badge}
                  foot={`${evidenceLine(r, all)} · ${nextFor(r)}`}
                  right={<Chip tone="warn">{priorityChip(r)}</Chip>}
                  onClick={() => setOpenProject(r.id)}
                  arrow
                />
              );
            })}
          </Divided>
        </Panel>

        <Panel floor>
          <PanelHead kicker="Reuse" title="Quick Wins ready to scale" />
          <div className="px-5 pb-4 md:px-6">
            <div className="font-sans text-[56px] font-semibold leading-none tracking-[-0.03em] tabular-nums text-ink">{quickWins.length}</div>
            <div className="mt-2 text-[12.5px] text-ink-dim">published for the company to use</div>
          </div>
          {/*
          <Divided>
            <ListRow
              mark={<Rank n={reviews} />}
              title="Delivery Project outputs ready for review"
              foot="Generalise before governed release"
              to="/aai"
              arrow
            />
          </Divided>
          */}
          <div className="px-5 py-4 md:px-6">
            <Action variant="quiet" to="/projects?tab=quick_wins">Review reusable Quick Wins</Action>
          </div>
        </Panel>
      </div>

      {/* <RegistryStrip className="mt-5" /> */}

      {/*
      <HomeResults
        ready={!overview.loading}
        title="Adoption and value"
        sub="Live results use reviewed evidence. Expected value remains a forecast until confirmed."
      >
        <ResultsBody />
      </HomeResults>
      */}
    </PageBody>
  );
};

export default ChampionHome;
